import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  // JD Basic Information
  jd_id: null,
  jd_guid: null,
  title: null,
  position: null,
  description: null,
  slug: null,

  // Skills
  must_have_skills: [],
  goodto_have_skills: [],

  // Additional Info
  experience_years: null,
  department: null,
  business_unit: null,
  employment_type: null,
  location: null,
  conversion_status: null,

  // Timestamps
  created_at: null,
  updated_at: null,

  // JD Custom Context (for this specific JD)
  customContext: null, // Custom context/instructions for this JD
  customQuestions: [], // JD-level custom questions (reusable for all candidates)

  // State flags
  isLoading: false,
  error: null,
};

const jdSlice = createSlice({
  name: 'jd',
  initialState,
  reducers: {
    // Set complete JD information
    setJDInfo: (state, action) => {
      const jdData = action.payload;
      state.jd_id = jdData.jd_id || null;
      state.jd_guid = jdData.jd_guid || null;
      state.title = jdData.title || null;
      state.position = jdData.position || null;
      state.description = jdData.description || null;
      state.slug = jdData.slug || null;
      state.must_have_skills = jdData.must_have_skills || [];
      state.goodto_have_skills = jdData.goodto_have_skills || [];
      state.experience_years = jdData.experience_years || null;
      state.department = jdData.department || null;
      state.business_unit = jdData.business_unit || null;
      state.employment_type = jdData.employment_type || null;
      state.location = jdData.location || null;
      state.conversion_status = jdData.conversion_status || null;
      state.created_at = jdData.created_at || null;
      state.updated_at = jdData.updated_at || null;
    },

    // Set individual JD fields
    setJDTitle: (state, action) => {
      state.title = action.payload;
    },

    setJDPosition: (state, action) => {
      state.position = action.payload;
    },

    setJDDescription: (state, action) => {
      state.description = action.payload;
    },

    setMustHaveSkills: (state, action) => {
      state.must_have_skills = action.payload;
    },

    setGoodToHaveSkills: (state, action) => {
      state.goodto_have_skills = action.payload;
    },

    setExperienceYears: (state, action) => {
      state.experience_years = action.payload;
    },

    setDepartment: (state, action) => {
      state.department = action.payload;
    },

    setLocation: (state, action) => {
      state.location = action.payload;
    },

    // Custom context and questions (JD-level)
    setCustomContext: (state, action) => {
      state.customContext = action.payload;
    },

    setCustomQuestions: (state, action) => {
      state.customQuestions = action.payload;
    },

    addCustomQuestion: (state, action) => {
      state.customQuestions.push(action.payload);
    },

    deleteCustomQuestion: (state, action) => {
      const index = action.payload;
      state.customQuestions.splice(index, 1);
    },

    updateCustomQuestion: (state, action) => {
      const { index, question } = action.payload;
      if (index >= 0 && index < state.customQuestions.length) {
        state.customQuestions[index] = question;
      }
    },

    // State flags
    setIsLoading: (state, action) => {
      state.isLoading = action.payload;
    },

    setError: (state, action) => {
      state.error = action.payload;
    },

    // Clear JD data
    clearJDData: (state) => {
      return initialState;
    },

    // Reset to initial state
    resetJD: (state) => {
      return initialState;
    },
  },
});

export const {
  setJDInfo,
  setJDTitle,
  setJDPosition,
  setJDDescription,
  setMustHaveSkills,
  setGoodToHaveSkills,
  setExperienceYears,
  setDepartment,
  setLocation,
  setCustomContext,
  setCustomQuestions,
  addCustomQuestion,
  deleteCustomQuestion,
  updateCustomQuestion,
  setIsLoading,
  setError,
  clearJDData,
  resetJD,
} = jdSlice.actions;

// Selectors
export const selectJDId = (state) => state.jd.jd_id;
export const selectJDGuid = (state) => state.jd.jd_guid;
export const selectJDTitle = (state) => state.jd.title;
export const selectJDPosition = (state) => state.jd.position;
export const selectJDDescription = (state) => state.jd.description;
export const selectJDSlug = (state) => state.jd.slug;
export const selectMustHaveSkills = (state) => state.jd.must_have_skills;
export const selectGoodToHaveSkills = (state) => state.jd.goodto_have_skills;
export const selectExperienceYears = (state) => state.jd.experience_years;
export const selectDepartment = (state) => state.jd.department;
export const selectBusinessUnit = (state) => state.jd.business_unit;
export const selectEmploymentType = (state) => state.jd.employment_type;
export const selectLocation = (state) => state.jd.location;
export const selectConversionStatus = (state) => state.jd.conversion_status;
export const selectCreatedAt = (state) => state.jd.created_at;
export const selectUpdatedAt = (state) => state.jd.updated_at;

// Custom context and questions
export const selectCustomContext = (state) => state.jd.customContext;
export const selectCustomQuestions = (state) => state.jd.customQuestions;
export const selectCustomQuestionsCount = (state) => state.jd.customQuestions.length;

// State flags
export const selectIsLoading = (state) => state.jd.isLoading;
export const selectError = (state) => state.jd.error;

// Composite selectors
export const selectAllJDInfo = (state) => ({
  jd_id: state.jd.jd_id,
  jd_guid: state.jd.jd_guid,
  title: state.jd.title,
  position: state.jd.position,
  description: state.jd.description,
  must_have_skills: state.jd.must_have_skills,
  goodto_have_skills: state.jd.goodto_have_skills,
  experience_years: state.jd.experience_years,
  department: state.jd.department,
  location: state.jd.location,
  employment_type: state.jd.employment_type,
});

export const selectAllSkills = (state) => [
  ...state.jd.must_have_skills,
  ...state.jd.goodto_have_skills,
];

export const selectTotalSkillsCount = (state) =>
  state.jd.must_have_skills.length + state.jd.goodto_have_skills.length;

export default jdSlice.reducer;
